import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Code, Copy, Check, ArrowRight, ShieldCheck, Webhook, Activity,
  Sparkles, ExternalLink,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_HOST = (process.env.REACT_APP_BACKEND_URL || "https://qortrace.com").replace(/\/$/, "");

// --- Snippet catalog ------------------------------------------------------
//
// Three flows × two languages. Snippets target the *production* API host
// (REACT_APP_BACKEND_URL at build time) so customers can copy-paste.

const SNIPPETS = {
  scan: {
    label: "Scan an address",
    icon: Activity,
    blurb: "Run a one-off PQC readiness scan and get a tier-graded score.",
    js: `// Node.js — POST /api/scan
const res = await fetch("${API_HOST}/api/scan", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": process.env.QORTRACE_API_KEY,   // optional for /scan
  },
  body: JSON.stringify({
    chain: "ethereum",
    address: "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
  }),
});
const result = await res.json();
console.log(result.scoring.tier, result.scoring.score);`,
    py: `# Python — POST /api/scan
import os, requests

r = requests.post(
    "${API_HOST}/api/scan",
    json={
        "chain":   "ethereum",
        "address": "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
    },
    headers={"X-API-Key": os.environ.get("QORTRACE_API_KEY", "")},  # optional
    timeout=20,
)
r.raise_for_status()
data = r.json()
print(data["scoring"]["tier"], data["scoring"]["score"])`,
  },
  audit: {
    label: "Run a smart-contract audit",
    icon: ShieldCheck,
    blurb: "Submit Solidity / Move / Rust source for the Standard tier audit. Poll until delivered.",
    js: `// Node.js — submit + poll a smart-contract audit
const create = await fetch("${API_HOST}/api/account/audits", {
  method: "POST",
  credentials: "include",          // session cookie OR
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": process.env.QORTRACE_API_KEY,
  },
  body: JSON.stringify({
    name: "MyToken",
    language: "solidity",
    chain: "ethereum",
    source_code: \`pragma solidity ^0.8.20;\\ncontract MyToken { /* ... */ }\`,
  }),
});
const { id } = await create.json();

// Poll until delivered (Standard tier is usually <90s).
while (true) {
  const r = await fetch(\`${API_HOST}/api/account/audits/\${id}\`,
                        { credentials: "include" });
  const audit = await r.json();
  if (audit.status === "delivered") {
    console.log("Score:", audit.security_score, "/100");
    console.log("Cert:", \`${API_HOST}/api/audit/\${id}/certificate.html\`);
    break;
  }
  await new Promise((r) => setTimeout(r, 3000));
}`,
    py: `# Python — submit + poll a smart-contract audit
import os, time, requests

API = "${API_HOST}"
HEADERS = {"X-API-Key": os.environ["QORTRACE_API_KEY"]}

resp = requests.post(
    f"{API}/api/account/audits",
    json={
        "name":     "MyToken",
        "language": "solidity",
        "chain":    "ethereum",
        "source_code": "pragma solidity ^0.8.20;\\ncontract MyToken { /* ... */ }",
    },
    headers=HEADERS,
    timeout=30,
)
resp.raise_for_status()
audit_id = resp.json()["id"]

while True:
    r = requests.get(f"{API}/api/account/audits/{audit_id}",
                     headers=HEADERS, timeout=15)
    audit = r.json()
    if audit["status"] == "delivered":
        print("Score:", audit["security_score"], "/100")
        print("Cert:", f"{API}/api/audit/{audit_id}/certificate.html")
        break
    time.sleep(3)`,
  },
  webhook: {
    label: "Receive + verify webhooks",
    icon: Webhook,
    blurb: "Stand up a signed-event receiver for monitoring.degraded + audit.delivered.",
    js: `// Node.js + Express — verify QorTrace webhook signatures
import express from "express";
import { createHmac, timingSafeEqual } from "node:crypto";

import useDocumentTitle from "../hooks/useDocumentTitle";

const SECRET = process.env.QORTRACE_WEBHOOK_SECRET;  // whsec_…
const app = express();

// IMPORTANT: capture the raw body. JSON middleware mutates whitespace and
// breaks signature verification.
app.post(
  "/qortrace/webhook",
  express.raw({ type: "application/json" }),
  (req, res) => {
    const sig = req.header("X-QorTrace-Signature") || "";
    const m = sig.match(/t=(\\d+),v1=([a-f0-9]+)/);
    if (!m) return res.status(400).send("bad sig");

    const [, ts, given] = m;
    if (Math.abs(Date.now() / 1000 - Number(ts)) > 300)
      return res.status(400).send("stale");

    const expected = createHmac("sha256", SECRET)
      .update(\`\${ts}.\${req.body}\`).digest("hex");

    const ok = timingSafeEqual(Buffer.from(expected, "hex"),
                                Buffer.from(given,    "hex"));
    if (!ok) return res.status(400).send("invalid sig");

    const event = JSON.parse(req.body.toString("utf-8"));
    console.log(event.event, event.data);    // monitoring.degraded etc.
    res.status(200).send("ok");
  },
);

app.listen(3001);`,
    py: `# Python + FastAPI — verify QorTrace webhook signatures
import hmac, hashlib, os, re, time
from fastapi import FastAPI, Request, HTTPException

SECRET = os.environ["QORTRACE_WEBHOOK_SECRET"]   # whsec_…
app = FastAPI()

@app.post("/qortrace/webhook")
async def receive(req: Request):
    raw = await req.body()                        # capture RAW bytes
    sig = req.headers.get("x-qortrace-signature", "")
    m = re.match(r"t=(\\d+),v1=([a-f0-9]+)", sig)
    if not m:
        raise HTTPException(400, "bad sig")
    ts, given = m.group(1), m.group(2)
    if abs(time.time() - int(ts)) > 300:
        raise HTTPException(400, "stale")

    expected = hmac.new(
        SECRET.encode(),
        f"{ts}.".encode() + raw,
        hashlib.sha256,
    ).hexdigest()
    if not hmac.compare_digest(expected, given):
        raise HTTPException(400, "invalid sig")

    event = (await req.json()) or {}
    print(event.get("event"), event.get("data"))   # monitoring.degraded etc.
    return {"ok": True}`,
  },
};

// --- Page -----------------------------------------------------------------

export default function SdkSamples() {
  useDocumentTitle('SDK Samples — QorTrace');
  const [active, setActive] = useState("scan");
  const [lang, setLang] = useState("js");
  const [copied, setCopied] = useState("");

  const onCopy = async (key, value) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? "" : k)), 1500);
    } catch (_) {}
  };

  const snip = SNIPPETS[active];
  const code = snip[lang];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--qt-bg)" }}>
      <Header />
      <main className="flex-1">
        <section className="max-w-[1100px] mx-auto px-6 sm:px-8 pt-16 pb-8">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>QORTRACE · DEVELOPER DOCS</div>
          <h1 className="qt-display text-4xl sm:text-5xl mt-2" style={{ color: "#fff" }}
              data-testid="sdks-headline">
            Copy-paste SDK samples
          </h1>
          <p className="mt-4 max-w-2xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            Three of the most common QorTrace integration flows in Node.js
            and Python. Drop these straight into your codebase, set your
            <code className="qt-mono text-xs"
                  style={{ color: "var(--qt-cyan)", marginLeft: 4 }}>QORTRACE_API_KEY</code>{" "}
            (and webhook secret) env vars, and you're live.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/tools/verify-webhook"
                  data-testid="sdks-verify-link"
                  className="qt-btn qt-btn-secondary"
                  style={{ fontSize: 12, padding: "0.45rem 0.8rem" }}>
              <ShieldCheck size={12} /> Webhook verifier <ArrowRight size={11} />
            </Link>
            <Link to="/account/api-keys"
                  data-testid="sdks-keys-link"
                  className="qt-btn qt-btn-secondary"
                  style={{ fontSize: 12, padding: "0.45rem 0.8rem" }}>
              <Sparkles size={12} /> Manage API keys <ArrowRight size={11} />
            </Link>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 sm:px-8 pb-16">
          {/* Flow tabs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {Object.entries(SNIPPETS).map(([id, s]) => {
              const Icon = s.icon;
              const isActive = active === id;
              return (
                <button key={id} type="button"
                        data-testid={`sdks-flow-${id}`}
                        onClick={() => setActive(id)}
                        className="qt-card text-left transition-colors"
                        style={{
                          padding: "1rem",
                          border: isActive ? "1.5px solid var(--qt-cyan)" : "1px solid var(--qt-border)",
                          background: isActive
                            ? "linear-gradient(135deg, rgba(0,229,255,0.06), transparent 60%)"
                            : "rgba(255,255,255,0.02)",
                          cursor: "pointer",
                        }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} style={{ color: isActive ? "var(--qt-cyan)" : "var(--qt-text-2)" }} />
                    <span className="qt-mono text-[11px]"
                          style={{
                            color: isActive ? "var(--qt-cyan)" : "var(--qt-text-2)",
                            letterSpacing: "0.10em", fontWeight: 700,
                          }}>
                      {s.label.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs"
                     style={{ color: "var(--qt-text-3)", lineHeight: 1.5 }}>
                    {s.blurb}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Code block */}
          <div className="qt-card" style={{ border: "1px solid var(--qt-border)", padding: "0.85rem" }}>
            <div className="flex items-center gap-2 mb-2">
              <Code size={13} style={{ color: "var(--qt-cyan)" }} />
              <span className="qt-mono text-[11px]"
                    style={{ color: "var(--qt-text-2)", letterSpacing: "0.10em" }}>
                {snip.label.toUpperCase()}
              </span>
              <div className="flex-1" />
              <button type="button"
                      onClick={() => setLang("js")}
                      data-testid="sdks-lang-js"
                      className="qt-mono text-[11px] px-3 py-1 rounded"
                      style={{
                        background: lang === "js" ? "var(--qt-cyan)" : "transparent",
                        color:      lang === "js" ? "#0A1020" : "var(--qt-text-2)",
                        border:     lang === "js" ? "1px solid var(--qt-cyan)" : "1px solid var(--qt-border)",
                        fontWeight: 700, letterSpacing: "0.10em",
                      }}>
                NODE.JS
              </button>
              <button type="button"
                      onClick={() => setLang("py")}
                      data-testid="sdks-lang-py"
                      className="qt-mono text-[11px] px-3 py-1 rounded"
                      style={{
                        background: lang === "py" ? "var(--qt-cyan)" : "transparent",
                        color:      lang === "py" ? "#0A1020" : "var(--qt-text-2)",
                        border:     lang === "py" ? "1px solid var(--qt-cyan)" : "1px solid var(--qt-border)",
                        fontWeight: 700, letterSpacing: "0.10em",
                      }}>
                PYTHON
              </button>
            </div>
            <div className="relative">
              <pre data-testid={`sdks-code-${active}-${lang}`}
                   className="qt-mono text-[11px]"
                   style={{
                     margin: 0, padding: "0.85rem 3rem 0.85rem 0.85rem",
                     background: "rgba(0,0,0,0.45)",
                     borderRadius: 6, color: "#D6E0EE",
                     whiteSpace: "pre", overflow: "auto",
                     lineHeight: 1.55, maxHeight: 480,
                   }}>{code}</pre>
              <button type="button"
                      onClick={() => onCopy(`${active}-${lang}`, code)}
                      data-testid={`sdks-copy-${active}-${lang}`}
                      className="qt-btn qt-btn-secondary"
                      style={{ position: "absolute", top: 8, right: 8,
                               fontSize: 11, padding: "0.3rem 0.55rem" }}>
                {copied === `${active}-${lang}`
                  ? <><Check size={11} /> Copied</>
                  : <><Copy size={11} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Helpful nudges below */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <Tip icon={Sparkles}
                 title="Get an API key"
                 body="Generate one in Account → API Keys. Pass it as the X-API-Key header."
                 to="/account/api-keys" testid="sdks-tip-keys" />
            <Tip icon={ShieldCheck}
                 title="Verify signatures"
                 body="Drop a payload + secret into the verifier and confirm before going live."
                 to="/tools/verify-webhook" testid="sdks-tip-verify" />
            <Tip icon={Webhook}
                 title="Subscribe to events"
                 body="monitoring.degraded · audit.delivered. 3-attempt retry with backoff."
                 to="/account/webhooks" testid="sdks-tip-webhooks" />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Tip({ icon: Icon, title, body, to, testid }) {
  return (
    <Link to={to} data-testid={testid}
          className="qt-card transition-colors"
          style={{
            padding: "0.9rem 1rem",
            border: "1px solid var(--qt-border)",
            background: "rgba(255,255,255,0.02)",
          }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={13} style={{ color: "var(--qt-cyan)" }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-cyan)", letterSpacing: "0.10em", fontWeight: 700 }}>
          {title.toUpperCase()}
        </span>
        <ExternalLink size={10} style={{ color: "var(--qt-text-3)", marginLeft: "auto" }} />
      </div>
      <p className="text-xs" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
        {body}
      </p>
    </Link>
  );
}
